import type {OperationDescriptor, RecordSourceSelectorProxy, Disposable, Variables} from 'relay-runtime'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {GraphQLTaggedNode} from 'react-relay'
import {createOperationDescriptor, getRequest} from 'relay-runtime'

/**
 * Stores the Relay data ID and query arguments needed to locate the
 * SearchResultItemConnection record that holds openIssueCount / closedIssueCount.
 */

interface SearchInfo {
  repoDataId: string
  queryArgs: {first: number; query: string; type: string; aggregations: boolean}
}

interface RetainedEntry {
  searchInfo: SearchInfo
  disposable: Disposable
}

const retainedEntries = new Map<string, RetainedEntry>()

// Build a stable key from the known query variables so each distinct filter combination gets its own entry.
function entryKey(variables: Variables): string {
  const owner = (variables.owner as string) ?? ''
  const name = (variables.name as string) ?? ''
  const query = (variables.query as string) ?? ''
  return `${owner}/${name}:${query}`
}

/**
 * Save the search record location and retain the query operation so the
 * search record survives navigation away from the list page.
 */
export function retainOpenClosedCounts(opts: {
  environment: {retain: (operation: OperationDescriptor) => Disposable}
  queryNode: GraphQLTaggedNode
  variables: Variables
  repoDataId: string
  queryArgs: SearchInfo['queryArgs']
}): void {
  const {environment, queryNode, variables, repoDataId, queryArgs} = opts

  const key = entryKey(variables)

  // Dispose previous retain for this exact query if any (e.g. remount)
  retainedEntries.get(key)?.disposable.dispose()

  const request = getRequest(queryNode)
  const operation = createOperationDescriptor(request, variables)

  const disposable = environment.retain(operation)

  retainedEntries.set(key, {searchInfo: {repoDataId, queryArgs}, disposable})
}

/**
 * Release all retained operations and clear state.
 */
export function releaseOpenClosedCounts(): void {
  for (const entry of retainedEntries.values()) {
    entry.disposable.dispose()
  }
  retainedEntries.clear()
}

/**
 * Adjust open/closed counts in the Relay store for every retained search query.
 *
 * @param store - Relay store proxy from the updater callback
 * @param delta - +1 for reopen, -1 for close
 */
export function adjustOpenClosedCounts(store: RecordSourceSelectorProxy, delta: number): void {
  if (retainedEntries.size === 0) return

  for (const {searchInfo} of retainedEntries.values()) {
    const repo = store.get(searchInfo.repoDataId)
    if (!repo) continue

    const search = repo.getLinkedRecord('search', searchInfo.queryArgs)
    if (!search) continue

    const openCount = (search.getValue('openIssueCount') as number) ?? 0
    const closedCount = (search.getValue('closedIssueCount') as number) ?? 0

    search.setValue(Math.max(0, openCount + delta), 'openIssueCount')
    search.setValue(Math.max(0, closedCount - delta), 'closedIssueCount')
  }
}
